import { useMutation, useQueryClient } from "@tanstack/react-query";
import { orpc } from "@/frontend/orpc-client";

export function useAddUserRecommendedSourceMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.addUserRecommendedSource.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listUserRecommendedSources.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useRemoveUserRecommendedSourceMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.removeUserRecommendedSource.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listUserRecommendedSources.key() });
      },
    })
  );
}

export function useDeletePatientMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.deletePatient.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listPatients.key() });
        queryClient.invalidateQueries({ queryKey: orpc.admin.getSamplePatientCount.key() });
        queryClient.invalidateQueries({ queryKey: orpc.org.listMembers.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

/**
 * Deletes EHR data for the signed-in individual workspace owner.
 * Refreshes every patient surface that can retain record-derived state.
 */
export function useDeleteOwnPatientRecordsMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.deleteOwnPatientRecords.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listPatients.key() });
        queryClient.invalidateQueries({ queryKey: orpc.admin.listPatientConnections.key() });
        queryClient.invalidateQueries({ queryKey: orpc.admin.getPatientRecordTypeCounts.key() });
        queryClient.invalidateQueries({ queryKey: orpc.admin.listUserAccounts.key() });
        queryClient.invalidateQueries({ queryKey: orpc.admin.getPatientRetrievalStatus.key() });
        if (onSuccess) onSuccess();
      },
    }),
  );
}

/** Saves edited patient details, refreshing the summary and table rows that display them. */
export function useUpdatePatientDetailsMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.updatePatientDetails.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.getPatientSummary.key() });
        queryClient.invalidateQueries({ queryKey: orpc.admin.listPatients.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useSetShowMobileAppMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.setShowMobileApp.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listPatients.key() });
        // The patient workspace reads this flag from the summary, not the list.
        queryClient.invalidateQueries({ queryKey: orpc.admin.getPatientSummary.key() });
      },
    })
  );
}

export function useDeletePatientSignatureMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.deletePatientSignature.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.getPatientSignature.key() });
      },
    })
  );
}

export function useSetAskConsentSignatureMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.setAskConsentSignature.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listPatients.key() });
        // The patient workspace reads this flag from the summary, not the list.
        queryClient.invalidateQueries({ queryKey: orpc.admin.getPatientSummary.key() });
      },
    })
  );
}

export function useTriggerPatientRetrievalMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.triggerPatientRetrieval.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.getPatientRetrievalStatus.key() });
      },
    })
  );
}


export function useGenerateMagicLinkMutation(onSuccess?: (data: { magicLinkUrl: string }) => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.generateMagicLink.mutationOptions({
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.getLatestMagicLink.key() });
        queryClient.invalidateQueries({ queryKey: orpc.admin.listMagicLinkActivity.key() });
        if (onSuccess) onSuccess(data);
      },
    })
  );
}

export function useCreateUserMutation(onSuccess?: (data: { id: string }) => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.createUser.mutationOptions({
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listPatients.key() });
        if (onSuccess) onSuccess(data);
      },
    })
  );
}
